<template>
    <app-dialog
        v-if="transaction"
        v-model="isActive"
        :persistent="transaction.$loading"
        :size="TransactionActionEnum.CHANGE_PRODUCT === actionId ? 'xx-large' : 'large'"
        :title="$t(`enums.transaction_action.${actionId}`)"
        :overline="$t('transaction')"
    >
        <template #default>
            <transition appear name="fade-transition" mode="out-in">
                <template v-if="TransactionActionEnum.CHANGE_AMOUNTS_AND_SHARES === actionId">
                    <v-card-text class="px-6 pt-1">
                        <p v-if="transaction.is_purchase">
                            {{ $t(`_page.transaction_action.${actionId}.PURCHASE`) }}
                        </p>
                        <p v-else-if="transaction.is_sale">
                            {{ $t(`_page.transaction_action.${actionId}.SALE`) }}
                        </p>
                        <field-money
                            ref="input.amount"
                            v-model="transaction.amount"
                            :disabled="transaction.$saving.length > 0"
                            :label="$t('amount')"
                            :decimals="6"
                            allow-minus
                            required
                        >
                            <template #append>
                                <app-button
                                    v-if="
                                        transaction.share_quantity &&
                                        transaction.share_price &&
                                        transaction.share_price.amount
                                    "
                                    :icon="getIcon('calculate')"
                                    :tooltip="transaction.amount_match ? null : $t('set_to_suggested_amount')"
                                    :disabled="transaction.amount_match"
                                    @click.stop="transaction.amount = transaction.calculated_amount"
                                />
                            </template>
                        </field-money>
                        <v-layout>
                            <v-flex xs6 class="mr-2">
                                <field-number
                                    ref="input.share_quantity"
                                    v-model="transaction.share_quantity"
                                    :disabled="transaction.$saving.length > 0"
                                    :label="$t('share_quantity')"
                                    required
                                >
                                    <template #append>
                                        <app-button
                                            v-if="
                                                transaction.amount &&
                                                transaction.amount.amount &&
                                                transaction.share_price &&
                                                transaction.share_price.amount
                                            "
                                            :icon="getIcon('calculate')"
                                            :tooltip="
                                                transaction.share_quantity_match ? null : $t('set_to_suggested_amount')
                                            "
                                            :disabled="transaction.share_quantity_match"
                                            @click.stop="
                                                transaction.share_quantity = transaction.calculated_share_quantity
                                            "
                                        />
                                    </template>
                                </field-number>
                            </v-flex>
                            <v-flex xs6 class="ml-2">
                                <field-money
                                    ref="input.share_price"
                                    v-model="transaction.share_price"
                                    :disabled="transaction.$saving.length > 0"
                                    :label="$t('share_price')"
                                    required
                                    :decimals="6"
                                >
                                    <template #append>
                                        <app-button
                                            v-if="
                                                transaction.amount &&
                                                transaction.amount.amount &&
                                                transaction.share_quantity
                                            "
                                            :icon="getIcon('calculate')"
                                            :tooltip="
                                                transaction.share_price_match ? null : $t('set_to_suggested_amount')
                                            "
                                            :disabled="transaction.share_price_match"
                                            @click.stop="transaction.share_price = transaction.calculated_share_price"
                                        />
                                    </template>
                                </field-money>
                            </v-flex>
                        </v-layout>
                        <transition appear name="slide-y-transition" mode="out-in">
                            <app-alert
                                v-if="
                                    transaction.amount &&
                                    transaction.share_quantity &&
                                    transaction.share_price &&
                                    (!transaction.share_price_match || !transaction.share_quantity_match)
                                "
                                alert-type="warning"
                                dense
                                title="Calculation Discrepancy"
                            >
                                <template #text>
                                    <p class="mt-1 mb-2 body-2 warning--text text--darken-2">
                                        {{ transaction.share_quantity }} shares at
                                        {{
                                            toCurrency(
                                                transaction.share_price.amount,
                                                transaction.share_price.currency,
                                                8
                                            )
                                        }}
                                        amounts to
                                        {{
                                            toCurrency(
                                                transaction.calculated_amount.amount,
                                                transaction.calculated_amount.currency,
                                                8
                                            )
                                        }}, which is a difference of

                                        <strong>{{
                                            toCurrency(
                                                transaction.amount_discrepancy.amount,
                                                transaction.amount_discrepancy.currency,
                                                8
                                            )
                                        }}</strong>
                                        from your total amount.
                                    </p>
                                    <p class="mb-0 body-2 warning--text text--darken-2">
                                        Please double-check your calculation to ensure accuracy. If these numbers are
                                        correct and the discrepancy is expected you can ignore this message.
                                    </p>
                                </template>
                            </app-alert>
                        </transition>
                    </v-card-text>
                </template>

                <template v-else-if="TransactionActionEnum.CHANGE_SALE_AMOUNT === actionId">
                    <v-card-text class="px-6 pt-1">
                        <p>{{ $t(`_page.transaction_action.${actionId}`) }}</p>
                        <field-money
                            ref="input.amount"
                            v-model="transaction.amount"
                            :disabled="transaction.$saving.length > 0"
                            :label="$t('amount')"
                            allow-minus
                            required
                        />
                    </v-card-text>
                </template>

                <template v-else-if="TransactionActionEnum.CHANGE_FUND_AMOUNT === actionId">
                    <v-card-text class="px-6 pt-1">
                        <p>{{ $t(`_page.transaction_action.${actionId}`) }}</p>
                        <field-money
                            ref="input.amount"
                            v-model="transaction.amount"
                            :disabled="transaction.$saving.length > 0"
                            :label="$t('amount')"
                            allow-minus
                            required
                        />
                    </v-card-text>
                </template>

                <template v-else-if="TransactionActionEnum.CHANGE_PRODUCT === actionId">
                    <div style="height: 100%">
                        <app-table
                            ref="input.product_id"
                            v-model="selectedProducts"
                            :headers="productsCollection.headers"
                            :request-items="productsCollection.request"
                            hide-column-visibility
                            show-quick-filters
                            lock-display
                            :selectable="1"
                            clickable
                            required
                            style="min-height: 20rem"
                            @input="transaction.product_id = $event && $event.length ? $event[0].id : null"
                        >
                            <template #heading="{ errorMessages }">
                                <div class="pl-6 mb-4 mr-4">
                                    <p class="mb-2">{{ $t(`_page.transaction_action.${actionId}`) }}</p>
                                    <app-alert
                                        alert-type="error"
                                        dense
                                        class="mb-2"
                                        :text="$t('danger_this_action_cannot_be_undone')"
                                    />
                                    <p
                                        v-for="(errorMessage, index) of errorMessages"
                                        :key="index"
                                        class="error--text mb-0"
                                    >
                                        {{ errorMessage }}
                                    </p>
                                </div>
                            </template>
                        </app-table>
                    </div>
                </template>

                <template v-else-if="TransactionActionEnum.TOGGLE_ON_OFF_PLATFORM === actionId">
                    <v-card-text class="px-6 pt-1">
                        <p v-if="transaction.is_purchase">
                            {{ $t(`_page.transaction_action.${actionId}.PURCHASE`) }}
                        </p>
                        <p v-else>{{ $t(`_page.transaction_action.${actionId}.OTHER`) }}</p>
                        <field-toggle
                            ref="input.on_platform"
                            v-model="transaction.on_platform"
                            :disabled="transaction.$saving.length > 0"
                            :label="$t('on_platform')"
                            :type="'checkbox'"
                        />
                    </v-card-text>
                </template>

                <template v-else-if="TransactionActionEnum.DELETE_TRANSACTION === actionId">
                    <v-card-text class="px-6 pt-1">
                        <p>{{ $t(`_page.transaction_action.${actionId}.warning`) }}</p>

                        <app-alert alert-type="error" dense :title="$t('danger_this_action_cannot_be_undone')">
                            <template
                                v-if="
                                    TransactionTypeEnum.PURCHASE === transaction.type ||
                                    TransactionTypeEnum.FUND_TRANSFER === transaction.type ||
                                    (TransactionTypeEnum.CASH_RECEIPT === transaction.type &&
                                        transaction.fund_investment_id)
                                "
                                #text
                            >
                                <p class="mt-1 mb-2 body-2 error--text text--darken-2">
                                    {{ $t(`_page.transaction_action.${actionId}.relationship_warning`) }}
                                </p>

                                <p class="mb-0 body-2 error--text text--darken-2">
                                    This action will fail if there are:
                                </p>
                                <ul class="mb-2">
                                    <li class="body-2 error--text text--darken-2">
                                        Transactions or documents linked to the holding.
                                    </li>
                                    <li class="body-2 error--text text--darken-2">Holdings still in the tranche.</li>
                                </ul>
                                <p class="mb-0 body-2 error--text text--darken-2">
                                    They must be removed before you can proceed.
                                </p>
                            </template>
                        </app-alert>

                        <h3 class="font-weight-normal mt-6">{{ $t('transaction') }} {{ $t('information') }}</h3>
                        <app-table-simple table-layout="auto" column-width-auto outlined>
                            <template #body>
                                <tr>
                                    <td>{{ $t('id') }}</td>
                                    <td>{{ transaction.id }}</td>
                                </tr>
                                <tr>
                                    <td>{{ $t('type') }}</td>
                                    <td>{{ $t(`enums.transaction_type.${transaction.type}`) }}</td>
                                </tr>
                                <tr v-if="_.get(transaction, 'amount.amount')">
                                    <td>{{ $t('amount') }}</td>
                                    <td>{{ toCurrency(transaction.amount.amount, transaction.amount.currency, 6) }}</td>
                                </tr>
                                <tr v-if="transaction.value_at">
                                    <td>{{ $t('value_at') }}</td>
                                    <td>
                                        <display-datetime :value="transaction.value_at" :options="{ showTime: true }" />
                                    </td>
                                </tr>
                                <tr v-if="_.get(transaction, 'gi_ref')">
                                    <td>{{ $t('gi_ref') }}</td>
                                    <td>{{ transaction.gi_ref }}</td>
                                </tr>
                                <tr>
                                    <td>{{ $t('on_platform') }}</td>
                                    <td><display-boolean :value="transaction.on_platform" /></td>
                                </tr>
                                <tr>
                                    <td>{{ $t('reconciled') }}</td>
                                    <td><display-boolean :value="transaction.reconciled" /></td>
                                </tr>
                                <tr>
                                    <td>{{ $t('locked') }}</td>
                                    <td><display-boolean :value="transaction.locked" /></td>
                                </tr>
                            </template>
                        </app-table-simple>

                        <template v-if="_.get(transaction, 'account.investor')">
                            <h3 class="font-weight-normal mt-6">{{ $t('investor') }} {{ $t('information') }}</h3>
                            <app-table-simple table-layout="auto" column-width-auto outlined>
                                <template #body>
                                    <tr>
                                        <td>{{ $t('name') }}</td>
                                        <td>{{ _.get(transaction, 'account.investor.name') }}</td>
                                    </tr>
                                    <tr>
                                        <td>{{ $t('gi_ref') }}</td>
                                        <td>{{ _.get(transaction, 'account.investor.gi_ref') }}</td>
                                    </tr>
                                </template>
                            </app-table-simple>
                        </template>

                        <template v-if="_.get(transaction, 'account.adviser')">
                            <h3 class="font-weight-normal mt-6">{{ $t('adviser') }} {{ $t('information') }}</h3>
                            <app-table-simple table-layout="auto" column-width-auto outlined>
                                <template #body>
                                    <tr>
                                        <td>{{ $t('name') }}</td>
                                        <td>{{ _.get(transaction, 'account.adviser.name') }}</td>
                                    </tr>
                                    <tr>
                                        <td>{{ $t('gi_ref') }}</td>
                                        <td>{{ _.get(transaction, 'account.adviser.gi_ref') }}</td>
                                    </tr>
                                </template>
                            </app-table-simple>
                        </template>

                        <template v-if="transaction.holding && (transaction.is_purchase || transaction.is_sale)">
                            <h3 class="font-weight-normal mt-6">{{ $t('holding') }} {{ $t('information') }}</h3>
                            <app-table-simple table-layout="auto" column-width-auto outlined>
                                <template #body>
                                    <tr>
                                        <td>{{ $t('id') }}</td>
                                        <td>{{ transaction.holding.id }}</td>
                                    </tr>
                                    <tr v-if="_.get(transaction, 'holding.share_quantity')">
                                        <td>{{ $t('share_quantity') }}</td>
                                        <td>{{ toNumber(transaction.holding.share_quantity) }}</td>
                                    </tr>
                                    <tr v-if="_.get(transaction, 'holding.share_issue_price')">
                                        <td>{{ $t('share_issue_price') }}</td>
                                        <td>
                                            {{
                                                toCurrency(
                                                    transaction.holding.share_issue_price.amount,
                                                    transaction.holding.share_issue_price.currency,
                                                    6
                                                )
                                            }}
                                        </td>
                                    </tr>
                                </template>
                            </app-table-simple>
                        </template>

                        <template v-if="transaction.account">
                            <h3 class="font-weight-normal mt-6">{{ $t('account') }} {{ $t('information') }}</h3>
                            <app-table-simple table-layout="auto" column-width-auto outlined>
                                <template #body>
                                    <tr>
                                        <td>{{ $t('id') }}</td>
                                        <td>{{ transaction.account.id }}</td>
                                    </tr>
                                    <tr>
                                        <td>{{ $t('name') }}</td>
                                        <td>{{ transaction.account.name }}</td>
                                    </tr>
                                    <tr v-if="_.get(transaction, 'account.investor.name')">
                                        <td>{{ $t('investor') }}</td>
                                        <td>{{ transaction.account.investor.name }}</td>
                                    </tr>
                                    <tr v-if="_.get(transaction, 'account.adviser.name')">
                                        <td>{{ $t('adviser') }}</td>
                                        <td>{{ transaction.account.adviser.name }}</td>
                                    </tr>
                                </template>
                            </app-table-simple>
                        </template>

                        <template v-if="transaction.fund_investment">
                            <h3 class="font-weight-normal mt-6">{{ $t('tranche') }} {{ $t('information') }}</h3>
                            <app-table-simple table-layout="auto" column-width-auto outlined>
                                <template #body>
                                    <tr>
                                        <td>{{ $t('id') }}</td>
                                        <td>{{ transaction.fund_investment.id }}</td>
                                    </tr>
                                    <tr v-if="_.get(transaction, 'fund_investment.invested_on')">
                                        <td>{{ $t('invested_on') }}</td>
                                        <td>
                                            <display-date :value="transaction.fund_investment.invested_on" />
                                        </td>
                                    </tr>
                                    <tr v-if="_.get(transaction, 'fund_investment.cash_balance')">
                                        <td>{{ $t('cash_balance') }}</td>
                                        <td>
                                            {{
                                                toCurrency(
                                                    transaction.fund_investment.cash_balance.amount,
                                                    transaction.fund_investment.cash_balance.currency,
                                                    6
                                                )
                                            }}
                                        </td>
                                    </tr>
                                    <tr v-if="_.get(transaction, 'fund_investment.total_investment')">
                                        <td>{{ $t('total_investment') }}</td>
                                        <td>
                                            {{
                                                toCurrency(
                                                    transaction.fund_investment.total_investment.amount,
                                                    transaction.fund_investment.total_investment.currency,
                                                    6
                                                )
                                            }}
                                        </td>
                                    </tr>
                                    <tr v-if="_.get(transaction, 'fund_investment.fund.name')">
                                        <td>{{ $t('fund') }}</td>
                                        <td>{{ transaction.fund_investment.fund.name }}</td>
                                    </tr>
                                    <tr v-if="_.get(transaction, 'fund_investment.investor.name')">
                                        <td>{{ $t('investor') }}</td>
                                        <td>{{ transaction.fund_investment.investor.name }}</td>
                                    </tr>
                                    <tr v-if="_.get(transaction, 'fund_investment.adviser.name')">
                                        <td>{{ $t('adviser') }}</td>
                                        <td>{{ transaction.fund_investment.adviser.name }}</td>
                                    </tr>
                                    <tr v-if="_.get(transaction, 'fund_investment.reconciled') !== null">
                                        <td>{{ $t('reconciled') }}</td>
                                        <td><display-boolean :value="transaction.fund_investment.reconciled" /></td>
                                    </tr>
                                </template>
                            </app-table-simple>
                        </template>
                    </v-card-text>
                </template>

                <template v-else>
                    <v-card-text class="px-6 pt-1">
                        <p>Action {{ actionId }} is still in development, please come back later.</p>
                    </v-card-text>
                </template>
            </transition>
        </template>
        <template #actions>
            <v-spacer />

            <app-button
                secondary
                :text="$t('cancel')"
                class="mr-2"
                :disabled="transaction.$loading"
                @click="isActive = false"
            />

            <app-button
                v-if="TransactionActionEnum.DELETE_TRANSACTION === actionId"
                :text="$t('delete')"
                :icon="getIcon('error')"
                :loading="transaction.$loading"
                class="ml-2"
                color="error"
                @click="deleteTransaction"
            />

            <app-button
                v-else
                :text="$t('save')"
                :icon="getIcon('save', 'fas')"
                :loading="transaction.$loading"
                class="ml-2"
                @click="saveTransaction"
            />
        </template>
    </app-dialog>
</template>

<script>
import { Transaction, ProductsCollection } from '@/models';
import TransactionTypeEnum from '@/enums/transaction/type';
import TransactionActionEnum from '@/enums/transaction/action';
import snack from '@/lib/helpers/snack';
import validate from '@/mixins/validate';
import dialog from '@/lib/helpers/dialog';

export default {
    name: 'TransactionActionModal',
    components: {
        AppDialog: () =>
            import(
                /* webpackChunkName: "core" */
                '@/components/AppDialog'
            ),
        AppTableSimple: () =>
            import(
                /* webpackChunkName: "core" */
                '@/components/table/AppTableSimple'
            )
    },
    mixins: [validate],
    props: {
        value: {
            type: Boolean,
            required: true
        },
        actionId: {
            type: String,
            required: true
        },
        transactionId: {
            type: String,
            required: true
        }
    },
    data: () => ({
        transaction: new Transaction(),
        TransactionTypeEnum,
        TransactionActionEnum,
        productsCollection: new ProductsCollection(),
        selectedProducts: []
    }),
    computed: {
        isActive: {
            get() {
                return this.value;
            },
            set(value) {
                this.$emit('input', value);
            }
        }
    },
    watch: {
        transactionId: {
            immediate: true,
            handler(id) {
                this.setTransaction(id);
            }
        }
    },
    methods: {
        async setTransaction(id) {
            const transaction = await Transaction.$get(id);
            this.$emit('input:transaction', new Transaction(transaction.$toPayload()));

            this.transaction = transaction;
        },
        getFields() {
            if (TransactionActionEnum.CHANGE_AMOUNTS_AND_SHARES === this.actionId) {
                return {
                    amount: this.transaction.amount,
                    share_quantity: this.transaction.share_quantity,
                    share_price: this.transaction.share_price
                };
            } else if (TransactionActionEnum.CHANGE_SALE_AMOUNT === this.actionId) {
                return {
                    amount: this.transaction.amount
                };
            } else if (TransactionActionEnum.CHANGE_FUND_AMOUNT === this.actionId) {
                return {
                    amount: this.transaction.amount
                };
            } else if (TransactionActionEnum.CHANGE_PRODUCT === this.actionId) {
                return {
                    product_id: this.transaction.product_id
                };
            } else if (TransactionActionEnum.TOGGLE_ON_OFF_PLATFORM === this.actionId) {
                return {
                    on_platform: this.transaction.on_platform
                };
            }

            return null;
        },
        async saveTransaction() {
            this.transaction.$loading = true;

            if (!this.validate()) {
                this.transaction.$loading = false;
                return;
            }

            try {
                const transaction = await this.transaction.$update(this.getFields());

                Transaction.delete(transaction.id);
                await this.setTransaction(transaction.id);
                this.isActive = false;

                snack({
                    timeout: 20000,
                    type: 'success',
                    text: `${this.$t('transaction')} ${this.$t('updated')}`
                });
            } catch (error) {
                this.handleError(error, this.saveTransaction);
            }

            this.transaction.$loading = false;
        },
        async deleteTransaction() {
            this.transaction.$loading = true;

            const removeItem = async () => {
                try {
                    await this.transaction.$remove();
                    this.$emit('delete:transaction', this.transaction);
                    this.isActive = false;
                    snack({
                        timeout: 20000,
                        type: 'success',
                        text: `${this.$t('transaction')} ${this.$t('deleted')}`
                    });
                } catch (error) {
                    this.handleError(error, removeItem);
                }
                this.transaction.$loading = false;
            };

            dialog({
                continueButtonColor: 'error',
                continueButtonIcon: 'error',
                continueButtonText: this.$t('delete'),
                cancelButtonText: this.$t('cancel'),
                title: this.$t('are_you_sure'),
                text:
                    this.$t('proceeding_will_permanently_delete') +
                    ' ' +
                    this.$t('transaction').toLocaleLowerCase() +
                    ' ' +
                    this.transaction.id +
                    '.',
                onContinueFn: removeItem,
                onCancelFn: () => {
                    this.transaction.$loading = false;
                }
            });
        }
    }
};
</script>
